/**
 * @fileoverview Reusable button component with Tailwind CSS.
 * Migrated from CSS modules to inline Tailwind for better maintainability.
 */

import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Size of the button */
  size?: ButtonSize;
  /** Full width button */
  fullWidth?: boolean;
  /** Show loading spinner */
  isLoading?: boolean;
  /** Icon to display before text */
  leftIcon?: ReactNode;
  /** Icon to display after text */
  rightIcon?: ReactNode;
  /** Button content */
  children: ReactNode;
}

// Base button styles
const baseStyles = [
  'inline-flex items-center justify-center gap-2',
  'font-medium leading-normal text-center no-underline whitespace-nowrap',
  'align-middle cursor-pointer select-none',
  'border border-transparent rounded-md',
  'transition-all duration-150 ease-in-out',
  'hover:enabled:-translate-y-px hover:enabled:shadow-lg',
  'active:enabled:translate-y-0',
  'disabled:opacity-65 disabled:cursor-not-allowed',
].join(' ');

// Variant styles
const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'text-white bg-accent border-accent hover:enabled:bg-accent-hover hover:enabled:border-accent-hover',
  secondary: 'text-white bg-text-secondary border-text-secondary hover:enabled:bg-opacity-80',
  outline:
    'text-accent bg-transparent border-accent hover:enabled:text-white hover:enabled:bg-accent',
  ghost: 'text-accent bg-transparent border-transparent hover:enabled:bg-accent/10',
  danger:
    'text-white bg-red-600 border-red-600 hover:enabled:bg-red-700 hover:enabled:border-red-700',
};

// Size styles
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-2 py-1 text-sm rounded',
  md: 'px-4 py-2 text-base rounded-md',
  lg: 'px-6 py-3 text-lg rounded-lg',
};

/**
 * A versatile button component with Tailwind CSS styling.
 *
 * @example
 * ```tsx
 * <Button variant="primary" onClick={handleClick}>
 *   Click me
 * </Button>
 *
 * <Button variant="outline" leftIcon={<Icon name="search" />}>
 *   Search
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={clsx(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'flex w-full',
          isLoading && 'relative text-transparent pointer-events-none',
          className
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <span
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                       w-4 h-4 border-2 border-current border-r-transparent 
                       rounded-full animate-spin"
            aria-hidden="true"
          />
        ) : (
          <>
            {leftIcon && <span className="inline-flex items-center">{leftIcon}</span>}
            <span className="inline-flex items-center">{children}</span>
            {rightIcon && <span className="inline-flex items-center">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
